import { Head, useForm, Link } from '@inertiajs/react'
import { useState } from 'react'
import Input from '../../components/ui/Input'
import Button from '../../components/ui/Button'

/**
 * ResetPassword page - Set new password after clicking reset link
 * Route: GET /identity/password-reset/edit
 * Form submits to: PATCH /identity/password-reset
 *
 * The token is passed as a URL parameter
 */
export default function ResetPassword({ token }) {
  const { data, setData, patch, processing, errors } = useForm({
    token: token || '',
    password: '',
    password_confirmation: ''
  })

  const [passwordStrength, setPasswordStrength] = useState('')

  const handlePasswordChange = (value) => {
    setData('password', value)

    // Calculate password strength
    if (value.length === 0) {
      setPasswordStrength('')
    } else if (value.length < 8) {
      setPasswordStrength('weak')
    } else if (value.length < 12 && /[A-Z]/.test(value) && /[0-9]/.test(value)) {
      setPasswordStrength('medium')
    } else if (value.length >= 12 && /[A-Z]/.test(value) && /[0-9]/.test(value) && /[^A-Za-z0-9]/.test(value)) {
      setPasswordStrength('strong')
    } else {
      setPasswordStrength('medium')
    }
  }

  const getPasswordStrengthColor = () => {
    switch (passwordStrength) {
      case 'weak': return 'bg-red-500'
      case 'medium': return 'bg-yellow-500'
      case 'strong': return 'bg-green-500'
      default: return 'bg-gray-300'
    }
  }

  const getPasswordStrengthText = () => {
    switch (passwordStrength) {
      case 'weak': return 'Faible'
      case 'medium': return 'Moyen'
      case 'strong': return 'Fort'
      default: return ''
    }
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    patch(`/identity/password_reset?sid=${data.token}`)
  }

  return (
    <>
      <Head title="Réinitialiser le mot de passe" />

      <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          {/* Header */}
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Nouveau mot de passe
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              Choisissez un nouveau mot de passe sécurisé pour votre compte
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="mt-8 space-y-6">
            <div className="space-y-4">
              <div>
                <Input
                  label="Nouveau mot de passe"
                  type="password"
                  name="password"
                  value={data.password}
                  onChange={(e) => handlePasswordChange(e.target.value)}
                  error={errors.password}
                  required
                  autoFocus
                  placeholder="••••••••"
                />

                {/* Password strength indicator */}
                {passwordStrength && (
                  <div className="mt-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                          style={{
                            width: passwordStrength === 'weak' ? '33%' :
                                   passwordStrength === 'medium' ? '66%' : '100%'
                          }}
                        />
                      </div>
                      <span className="text-xs font-medium text-gray-600">
                        {getPasswordStrengthText()}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Utilisez 12+ caractères avec majuscules, chiffres et symboles
                    </p>
                  </div>
                )}
              </div>

              <Input
                label="Confirmer le mot de passe"
                type="password"
                name="password_confirmation"
                value={data.password_confirmation}
                onChange={(e) => setData('password_confirmation', e.target.value)}
                error={errors.password_confirmation}
                required
                placeholder="••••••••"
              />

              {/* Password requirements */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-900 mb-2">
                  Exigences du mot de passe :
                </h4>
                <ul className="text-xs text-blue-800 space-y-1">
                  <li className="flex items-start">
                    <svg
                      className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Minimum 8 caractères (12+ recommandé)
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Au moins une majuscule et un chiffre
                  </li>
                  <li className="flex items-start">
                    <svg
                      className="h-4 w-4 text-blue-600 mr-2 flex-shrink-0"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Un caractère spécial pour plus de sécurité
                  </li>
                </ul>
              </div>
            </div>

            {/* Submit button */}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              className="w-full"
              disabled={processing}
              loading={processing}
            >
              Réinitialiser le mot de passe
            </Button>

            {/* Back to sign in */}
            <div className="text-center">
              <Link
                href="/sign-in"
                className="inline-flex items-center text-sm font-medium text-primary-600 hover:text-primary-700 transition-colors"
              >
                <svg
                  className="mr-2 h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                Retour à la connexion
              </Link>
            </div>
          </form>
        </div>
      </div>
    </>
  )
}
